import { useEffect, useMemo, useState } from 'react'
import html2pdf from 'html2pdf.js'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { AREA_LABEL, TABLE_LABEL, ACTION_LABEL, csvRows, toCsv, CSV_HEADER, recordName, utc } from '../lib/auditCore'
import { HistoryEntry, canSeeAllHistory } from '../components/HistoryLink'

// Change history: who changed what, when - every change by hand, by the AI or by a script (sql/audit-log.sql).
// For ISM / inspections: filter by date, person, area, record; export CSV and PDF.
const LIMIT = 2000
const iso = d => d.toISOString().slice(0, 10)
const esc = s => String(s == null ? '' : s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))

export default function ChangeHistory() {
  const auth = useAuth()
  const { config } = auth
  const all = canSeeAllHistory(auth)
  const [from, setFrom] = useState(iso(new Date(Date.now() - 30 * 864e5)))
  const [to, setTo] = useState(iso(new Date()))
  const [area, setArea] = useState('')
  const [record, setRecord] = useState('')
  const [person, setPerson] = useState('')
  const [action, setAction] = useState('')
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [q, setQ] = useState('')   // record text actually searched (after a short pause)
  useEffect(() => { const t = setTimeout(() => setQ(record.trim()), 350); return () => clearTimeout(t) }, [record])

  useEffect(() => {
    if (!config?.id || !all) return
    let live = true
    setLoading(true); setErr('')
    let s = supabase.from('audit_log').select('*')   // * = also the approval comment (sql/quick-wins.sql) once it exists
    
      .eq('client_id', config.id).gte('at', from + 'T00:00:00Z').lt('at', iso(new Date(Date.parse(to + 'T00:00:00Z') + 864e5)) + 'T00:00:00Z')
    if (area) s = s.eq('area', area)
    if (action) s = s.eq('action', action)
    if (q) s = s.ilike('record_label', '%' + q.replace(/[%_]/g, '') + '%')
    s.order('at', { ascending: false }).limit(LIMIT).then(({ data, error }) => {
      if (!live) return
      if (error) setErr(/audit_log/.test(error.message || '') ? 'The change history is not set up yet: run sql/audit-log.sql in Supabase.' : 'Could not load the change history: ' + error.message)
      setRows(data || []); setLoading(false)
    })
    return () => { live = false }
  }, [config?.id, all, from, to, area, action, q])

  const people = useMemo(() => [...new Set(rows.map(r => r.user_name || 'Unknown user'))].sort(), [rows])
  const shown = person ? rows.filter(r => (r.user_name || 'Unknown user') === person) : rows
  const fileBase = ('Change_history_' + (config?.vessel_name || 'vessel') + '_' + from + '_' + to).replace(/[^A-Za-z0-9_-]+/g, '_')
  const filterText = [from + ' to ' + to, area && AREA_LABEL[area], person, action && ACTION_LABEL[action], q && '"' + q + '"'].filter(Boolean).join(' · ')

  function exportCsv() {
    const blob = new Blob([toCsv(csvRows(shown))], { type: 'text/csv;charset=utf-8' })
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = fileBase + '.csv'
    document.body.appendChild(a); a.click(); setTimeout(() => { URL.revokeObjectURL(a.href); a.remove() }, 500)
  }
  function exportPdf() {
    const body = csvRows(shown).map(r => '<tr>' + r.map(c => '<td>' + esc(c) + '</td>').join('') + '</tr>').join('')
    const el = document.createElement('div')
    el.innerHTML = `<div style="font-family:Arial,sans-serif;color:#111;padding:6px;width:1100px">
      <div style="font-size:18px;font-weight:700">Change history — ${esc(config?.vessel_name || '')}</div>
      <div style="font-size:11px;color:#555;margin:4px 0 10px">${esc(filterText)} · ${shown.length} changes · printed ${esc(utc(new Date()))}</div>
      <table style="border-collapse:collapse;width:100%;font-size:9.5px">
        <thead><tr>${CSV_HEADER.map(h => '<th style="text-align:left;border-bottom:1.5px solid #333;padding:4px 5px">' + esc(h) + '</th>').join('')}</tr></thead>
        <tbody>${body.replace(/<td>/g, '<td style="border-bottom:0.5px solid #ccc;padding:3px 5px;vertical-align:top;word-break:break-word">')}</tbody>
      </table>
      <div style="font-size:9px;color:#5b6878;margin-top:10px">Recorded automatically by the database for every change; entries cannot be edited or deleted.</div></div>`
    document.body.appendChild(el)
    html2pdf().set({ margin: 6, filename: fileBase + '.pdf', image: { type: 'jpeg', quality: 0.95 }, html2canvas: { scale: 2, windowWidth: 1120, width: 1120 }, pagebreak: { mode: ['css', 'legacy'], avoid: 'tr' }, jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' } })
      .from(el).save().then(() => document.body.removeChild(el))
  }

  if (!all) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>The change history is for the Captain and management. Your own changes are under &ldquo;History&rdquo; on each record.</div>

  const inp = { padding: '7px 9px', borderRadius: 8, border: '0.5px solid var(--border)', background: 'var(--hull)', color: 'var(--text)', fontSize: 12.5, minWidth: 0 }
  const lab = { fontSize: 10, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--muted)', display: 'block', marginBottom: 3 }
  return (
    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 1100 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, alignItems: 'end' }}>
        <label><span style={lab}>From</span><input type="date" value={from} max={to} onChange={e => e.target.value && setFrom(e.target.value)} style={{ ...inp, width: '100%' }} /></label>
        <label><span style={lab}>To</span><input type="date" value={to} min={from} onChange={e => e.target.value && setTo(e.target.value)} style={{ ...inp, width: '100%' }} /></label>
        <label><span style={lab}>Person</span><select value={person} onChange={e => setPerson(e.target.value)} style={{ ...inp, width: '100%' }}><option value="">Everyone</option>{people.map(p => <option key={p} value={p}>{p}</option>)}</select></label>
        <label><span style={lab}>Area</span><select value={area} onChange={e => setArea(e.target.value)} style={{ ...inp, width: '100%' }}><option value="">All areas</option>{Object.entries(AREA_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>
        <label><span style={lab}>Action</span><select value={action} onChange={e => setAction(e.target.value)} style={{ ...inp, width: '100%' }}><option value="">All</option>{Object.entries(ACTION_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>
        <label><span style={lab}>Record</span><input value={record} placeholder="e.g. liferaft" onChange={e => setRecord(e.target.value)} style={{ ...inp, width: '100%' }} /></label>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: 'var(--muted)', flex: 1, minWidth: 160 }}>{loading ? 'Loading…' : shown.length + (shown.length === 1 ? ' change' : ' changes') + (rows.length >= LIMIT ? ' (first ' + LIMIT + ' - narrow the dates to see more)' : '')}</span>
        <button className="btn" disabled={!shown.length} onClick={exportCsv}>Export CSV</button>
        <button className="btn" disabled={!shown.length} onClick={exportPdf}>Export PDF</button>
      </div>
      {err && <div style={{ color: '#b4313c', fontSize: 13 }}>{err}</div>}
      <div style={{ background: 'var(--hull)', border: '0.5px solid var(--border)', borderRadius: 12, padding: '4px 14px' }}>
        {!loading && !shown.length && !err && <div style={{ fontSize: 13, color: 'var(--muted)', padding: '14px 0' }}>No changes for these filters.</div>}
        {shown.map(e => (
          <HistoryEntry key={e.id} e={e} showRecord={
            <span style={{ color: 'var(--text)' }}>
              <span style={{ color: 'var(--muted)' }}>{(TABLE_LABEL[e.table_name] || e.table_name)}{e.area && AREA_LABEL[e.area] && TABLE_LABEL[e.table_name] !== AREA_LABEL[e.area] ? ' (' + AREA_LABEL[e.area] + ')' : ''}: </span>
              <button type="button" onClick={() => setRecord(e.record_label || '')} title="Show only this record" style={{ background: 'none', border: 'none', padding: 0, color: 'var(--text)', cursor: 'pointer', fontSize: 12.5, textDecoration: 'underline dotted' }}>{recordName(e)}</button>
            </span>
          } />
        ))}
      </div>
    </div>
  )
}
